import * as React from 'react';
import { Message, ToolCallState } from './types';
import AiChatDataCard from './AiChatDataCard';
import AiChatConfirm from './AiChatConfirm';
import { MarkdownRenderer } from './MarkdownRenderer';
import AiAvatar from './AiAvatar';
import WidgetRenderer from './widgets/WidgetRenderer';
import FlairIcon from '../../../../atomic/atoms/FlairIcon';
import { stripQuickActionsComment } from './stripQuickActionsComment';

type Props = {
  message: Message;
  pendingConfirm: ToolCallState | null;
  onConfirm: (toolId: string) => void;
  onCancel: (toolId: string) => void;
  onFeedback?: (
    messageId: string,
    rating: 'up' | 'down',
    comment?: string,
  ) => void;
};

const AiChatMessage: React.FC<Props> = ({
  message,
  pendingConfirm,
  onConfirm,
  onCancel,
  onFeedback,
}) => {
  const isUser = message.role === 'user';
  const content = message.content
    ? stripQuickActionsComment(message.content)
    : '';

  const hasConfirmForThisMessage =
    pendingConfirm &&
    message.toolCalls.some((tc) => tc.id === pendingConfirm.id);

  // Don't render empty assistant messages (they'll show the loader instead)
  if (
    !isUser &&
    !content &&
    message.dataCards.length === 0 &&
    (message.widgets ?? []).length === 0 &&
    !hasConfirmForThisMessage
  ) {
    return null;
  }

  const canFeedback =
    !isUser &&
    !!onFeedback &&
    !!message.runId &&
    !hasConfirmForThisMessage &&
    (content.length > 0 || (message.widgets ?? []).length > 0);

  return (
    <div
      style={{
        display: 'flex',
        gap: '0.75rem',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
      }}>
      {!isUser && <AiAvatar size="sm" />}
      <div
        className={
          isUser ? 'ai-chat-msg-user' : 'ai-chat-msg-assistant ai-chat-markdown'
        }>
        {(message.widgets ?? []).map((widget, idx) => (
          <WidgetRenderer key={`widget-${idx}`} widget={widget} />
        ))}

        {hasConfirmForThisMessage && pendingConfirm && (
          <AiChatConfirm
            toolCall={pendingConfirm}
            onConfirm={onConfirm}
            onCancel={onCancel}
          />
        )}

        {content && !hasConfirmForThisMessage && (
          <MarkdownRenderer>{content}</MarkdownRenderer>
        )}

        {(message.widgets ?? []).length === 0 &&
          message.dataCards.map((card, idx) => (
            <AiChatDataCard key={idx} dataCard={card} />
          ))}

        {canFeedback && (
          <FeedbackControls
            message={message}
            onFeedback={onFeedback as NonNullable<Props['onFeedback']>}
          />
        )}
      </div>
    </div>
  );
};

type FeedbackControlsProps = {
  message: Message;
  onFeedback: NonNullable<Props['onFeedback']>;
};

const FeedbackControls: React.FC<FeedbackControlsProps> = ({
  message,
  onFeedback,
}) => {
  const [showCommentInput, setShowCommentInput] = React.useState(false);
  const [comment, setComment] = React.useState('');
  const rating = message.feedback;

  const handleThumb = (value: 'up' | 'down') => {
    if (rating) return;
    onFeedback(message.id, value);
    if (value === 'down') setShowCommentInput(true);
  };

  const submitComment = () => {
    const trimmed = comment.trim();
    if (trimmed.length > 0) {
      onFeedback(message.id, 'down', trimmed);
    }
    setShowCommentInput(false);
  };

  return (
    <div className="ai-chat-feedback" data-rated={rating ?? ''}>
      {(!rating || rating === 'up') && (
        <button
          type="button"
          aria-label="Helpful response"
          className={`ai-chat-feedback-btn${
            rating === 'up' ? ' ai-chat-feedback-btn--active-up' : ''
          }`}
          onClick={() => handleThumb('up')}
          disabled={!!rating}>
          <FlairIcon icon="phosphor-thumbs-up" />
        </button>
      )}
      {(!rating || rating === 'down') && (
        <button
          type="button"
          aria-label="Unhelpful response"
          className={`ai-chat-feedback-btn${
            rating === 'down' ? ' ai-chat-feedback-btn--active-down' : ''
          }`}
          onClick={() => handleThumb('down')}
          disabled={!!rating}>
          <FlairIcon icon="phosphor-thumbs-down" />
        </button>
      )}
      {showCommentInput && (
        <input
          type="text"
          autoFocus
          className="ai-chat-feedback-comment"
          placeholder="What went wrong?"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onBlur={submitComment}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submitComment();
            if (e.key === 'Escape') setShowCommentInput(false);
          }}
        />
      )}
    </div>
  );
};

export default AiChatMessage;
